import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';

import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { ImpostosImpressosSelecaoComponent } from '../../../../shared/components/impostos-impressos-selecao/impostos-impressos-selecao.component';
import {
  PageScrollSpyComponent,
  ScrollSpySection,
} from '../../../../shared/components/page-scroll-spy/page-scroll-spy.component';

import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';

import { ConfigOrcamentoService } from '../../../../shared/services/apis/vendas/config-orcamento.service';
import { ConfigOrcamentoRequest } from '../../../../shared/services/requests/vendas/config-orcamento.request';
import {
  TipoImpostoImpressao,
  TipoImpressaoImagem,
} from '../../../../shared/services/models/vendas/config-orcamento.model';

@Component({
  selector: 'app-config-orcamento',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatIconModule,
    MatButtonModule,
    NgxEditorModule,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
    PageScrollSpyComponent,
    ImpostosImpressosSelecaoComponent,
  ],
  templateUrl: './config-orcamento.component.html',
  styleUrls: ['./config-orcamento.component.css'],
})
export class ConfigOrcamentoComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Vendas;
  public form: FormGroup;
  public isLoading = false;
  public isSaving = false;

  private idConfigOrcamento = 0;

  // Só libera o Salvar depois que a configuração da empresa realmente chegou. Salvar com o
  // carregamento falho gravaria os defaults do formulário sobre o que está no banco.
  public configCarregada = false;

  public opcoesTipoImpressaoImagem: TipoImpressaoImagem[] = [];
  public opcoesTipoImpostoImpressao: TipoImpostoImpressao[] = [];
  public impostosSelecionados: number[] = [];

  // Menu de navegação das seções, no padrão de config-geral-vendas.
  public sections: ScrollSpySection[] = [
    { id: 'regras-bloqueios', label: 'Regras e bloqueios' },
    { id: 'impressao', label: 'Impressão' },
    { id: 'observacoes', label: 'Observações' },
    { id: 'textos-padrao', label: 'Textos padrão' },
  ];

  // Dois editores independentes: Observação padrão e Introdução. Criados no ngOnInit e destruídos no
  // ngOnDestroy — instância de ngx-editor não sobrevive à saída da tela.
  public editorObservacao!: Editor;
  public editorIntroducao!: Editor;

  // Ordem dos grupos definida pela story. Sem link, imagem, tabela, código, h4+ nem "limpar formatação".
  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3'] }],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  public readonly validadeApoio =
    'Quando ativo, todo orçamento novo expira automaticamente após o prazo definido. Para um prazo maior em ' +
    'um orçamento específico, é necessária liberação de gerente. Os novos orçamentos já nascem com um prazo de ' +
    'validade definido (para estender, solicite a aprovação do gerente). A reserva de estoque acompanha essa ' +
    'mesma validade e é liberada no dia seguinte ao vencimento. Alterar o prazo do orçamento ajusta a reserva ' +
    'automaticamente. Se preferir que as reservas nunca expirem, mantenha a função de expiração desligada.';

  public readonly textosPadraoApoio =
    'Preenchidos automaticamente ao criar um orçamento. Quando a edição não é permitida, o vendedor não ' +
    'altera o texto no documento.';

  constructor(
    private formBuilder: FormBuilder,
    private configService: ConfigOrcamentoService,
    private alertService: AlertService,
    private router: Router
  ) {
    this.form = this.formBuilder.group({
      // Regras e bloqueios
      vendedorObrigatorio: [true],
      calcularImpostos: [true],
      isOrcamentoExpiraAutomatico: [false],
      prazoValidadeDias: [null],
      isPrazoValidadeEditavel: [false],
      // Impressão
      imprimirNcm: [false],
      imprimirDescComplementar: [true],
      imprimirEmailUsuario: [false],
      imprimirImpostosItens: [false],
      idTipoImpressaoImagem: [1],
      // Observações
      observacaoPadrao: [''],
      // Textos padrão
      textoIntroducao: [''],
      isIntroducaoEditavel: [true],
      assinaturaSaudacao: [''],
      assinaturaResponsavel: [''],
      isAssinaturaEditavel: [true],
    });
  }

  ngOnInit(): void {

    this.resetScrollToTop();

    this.editorObservacao = new Editor();
    this.editorIntroducao = new Editor();

    // As duas dependências condicionais reagem só à ação do usuário: o carregamento aplica os valores
    // com emitEvent:false e depois chama os mesmos métodos sem descartar o que veio do banco.
    this.form.get('calcularImpostos')?.valueChanges.subscribe(() => this.aplicarDependenciaImpostos(true));
    this.form.get('isOrcamentoExpiraAutomatico')?.valueChanges.subscribe(() => this.aplicarRegrasValidade(true));

    this.carregarDados();
  }

  ngOnDestroy(): void {
    this.editorObservacao?.destroy();
    this.editorIntroducao?.destroy();
  }

  private carregarDados(): void {
    this.isLoading = true;
    forkJoin({
      config: this.configService.get().pipe(catchError(() => of(null))),
      lookups: this.configService.getLookups().pipe(catchError(() => of(null))),
    }).subscribe({
      next: res => {
        this.isLoading = false;

        if (res.lookups && !res.lookups.isError) {
          this.opcoesTipoImpressaoImagem = res.lookups.listTipoImpressaoImagem || [];
          this.opcoesTipoImpostoImpressao = res.lookups.listTipoImpostoImpressao || [];
        } else if (res.lookups?.isError) {
          this.alertService.show('Aviso', res.lookups.errorDescription, AlertType.warning);
        }

        if (res.config && !res.config.isError && res.config.configOrcamento) {
          const c = res.config.configOrcamento;
          this.idConfigOrcamento = c.idConfigOrcamento;
          this.form.patchValue(
            {
              vendedorObrigatorio: c.vendedorObrigatorio,
              calcularImpostos: c.calcularImpostos,
              isOrcamentoExpiraAutomatico: c.isOrcamentoExpiraAutomatico,
              prazoValidadeDias: c.prazoValidadeDias,
              isPrazoValidadeEditavel: c.isPrazoValidadeEditavel,
              imprimirNcm: c.imprimirNcm,
              imprimirDescComplementar: c.imprimirDescComplementar,
              imprimirEmailUsuario: c.imprimirEmailUsuario,
              imprimirImpostosItens: c.imprimirImpostosItens,
              idTipoImpressaoImagem: c.idTipoImpressaoImagem,
              observacaoPadrao: c.observacaoPadrao || '',
              textoIntroducao: c.textoIntroducao || '',
              isIntroducaoEditavel: c.isIntroducaoEditavel,
              assinaturaSaudacao: c.assinaturaSaudacao || '',
              assinaturaResponsavel: c.assinaturaResponsavel || '',
              isAssinaturaEditavel: c.isAssinaturaEditavel,
            },
            { emitEvent: false }
          );
          this.impostosSelecionados = res.config.listImpostoImpressao || [];
          this.configCarregada = true;
        } else if (res.config?.isError) {
          this.alertService.show('Aviso', res.config.errorDescription, AlertType.warning);
        } else {
          // O `catchError` acima devolve null quando o GET falha, e esse caso não cai em nenhum dos
          // ramos anteriores. Sem este aviso a tela abria silenciosamente com os defaults do
          // formulário, e um Salvar gravaria esses defaults sobre a configuração real da empresa.
          this.alertService.show(
            'Aviso',
            'Não foi possível carregar as configurações dos orçamentos. Recarregue a tela antes de salvar.',
            AlertType.warning
          );
        }

        // Espelha nos controles dependentes o estado que veio do banco, sem resetar nada.
        this.aplicarDependenciaImpostos(false);
        this.aplicarRegrasValidade(false);

        this.resetScrollToTop();
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
        this.resetScrollToTop();
      },
    });
  }

  /**
   * Só faz sentido imprimir impostos quando eles são calculados. Com o cálculo desligado, a chave de
   * impressão fica desmarcada e desabilitada (e é gravada como falsa); ao religar o cálculo, ela volta
   * habilitada e desmarcada — `reset` é o que distingue a ação do usuário do reflexo do valor gravado.
   */
  private aplicarDependenciaImpostos(reset: boolean): void {
    const impressao = this.form.get('imprimirImpostosItens');
    if (!impressao) return;

    if (this.form.get('calcularImpostos')?.value) {
      impressao.enable({ emitEvent: false });
      if (reset) impressao.setValue(false, { emitEvent: false });
      return;
    }

    impressao.setValue(false, { emitEvent: false });
    impressao.disable({ emitEvent: false });
  }

  /**
   * Com a expiração ligada, o prazo é obrigatório e inteiro maior que zero. Ao desligar, o prazo e a
   * permissão deixam de ser aplicados e os valores informados são descartados na gravação.
   */
  private aplicarRegrasValidade(limpar: boolean): void {
    const prazo = this.form.get('prazoValidadeDias');
    if (!prazo) return;

    if (this.form.get('isOrcamentoExpiraAutomatico')?.value) {
      prazo.setValidators([Validators.required, Validators.min(1), Validators.pattern(/^\d+$/)]);
      prazo.updateValueAndValidity({ emitEvent: false });
      return;
    }

    prazo.clearValidators();
    if (limpar) {
      prazo.setValue(null, { emitEvent: false });
      this.form.get('isPrazoValidadeEditavel')?.setValue(false, { emitEvent: false });
    }
    prazo.updateValueAndValidity({ emitEvent: false });
  }

  private resetScrollToTop(): void {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }

  public get mostrarImpostos(): boolean {
    return !!this.form.get('calcularImpostos')?.value && !!this.form.get('imprimirImpostosItens')?.value;
  }

  public get prazoValidadeInvalido(): boolean {
    const prazo = this.form.get('prazoValidadeDias');
    return !!prazo?.invalid && (!!prazo.touched || !!prazo.dirty);
  }

  public onImpostosChange(ids: number[]): void {
    this.impostosSelecionados = ids;
    this.form.markAsDirty();
  }

  public salvar(): void {
    // Guarda também aqui, não só no botão: sem a configuração carregada, o PUT gravaria os defaults
    // do formulário e apagaria observação, textos padrão e a seleção de impostos da empresa.
    if (!this.configCarregada) {
      this.alertService.show(
        'Atenção',
        'As configurações não foram carregadas. Recarregue a tela antes de salvar.',
        AlertType.error
      );
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.alertService.show('Atenção', 'Preencha os campos obrigatórios corretamente.', AlertType.error);
      return;
    }

    this.isSaving = true;
    const raw = this.form.getRawValue();
    const expira = !!raw.isOrcamentoExpiraAutomatico;

    const request: ConfigOrcamentoRequest = {
      vendedorObrigatorio: raw.vendedorObrigatorio,
      imprimirNcm: raw.imprimirNcm,
      imprimirDescComplementar: raw.imprimirDescComplementar,
      imprimirEmailUsuario: raw.imprimirEmailUsuario,
      calcularImpostos: raw.calcularImpostos,
      imprimirImpostosItens: raw.calcularImpostos && raw.imprimirImpostosItens,
      idTipoImpressaoImagem: raw.idTipoImpressaoImagem,
      observacaoPadrao: raw.observacaoPadrao,
      // Expiração desligada descarta prazo e permissão.
      isOrcamentoExpiraAutomatico: expira,
      prazoValidadeDias: expira ? Number(raw.prazoValidadeDias) : null,
      isPrazoValidadeEditavel: expira && !!raw.isPrazoValidadeEditavel,
      textoIntroducao: raw.textoIntroducao,
      isIntroducaoEditavel: raw.isIntroducaoEditavel,
      assinaturaSaudacao: (raw.assinaturaSaudacao || '').trim() || null,
      assinaturaResponsavel: (raw.assinaturaResponsavel || '').trim() || null,
      isAssinaturaEditavel: raw.isAssinaturaEditavel,
      // A seleção é preservada mesmo com a impressão de impostos desligada.
      listImpostoImpressao: this.impostosSelecionados,
    };

    this.configService.update(this.idConfigOrcamento, request).subscribe({
      next: response => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', 'Configurações salvas com sucesso!', AlertType.success);
        this.voltar();
      },
      error: error => {
        this.isSaving = false;
        this.alertService.show('Aviso', error || 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/vendas']);
  }
}
